import { AlertCircle, ImageIcon, Sparkles } from 'lucide-react';
import { Input } from '../../../ui/input';
import { Switch } from '../../../ui/switch';
import { WB_W, WB_H, WhiteboardShapes } from './whiteboardShapes';

export function ErrorBox({ error, testid = 'wb-ai-error' }) {
  if (!error) return null;
  return (
    <div data-testid={testid} className="flex items-start gap-2 p-3 rounded-md bg-red-50 text-red-700 text-sm">
      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
    </div>
  );
}

export function AiPreviewCanvas({ objects, background }) {
  if (!objects?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" style={{ backgroundColor: background || '#FFFFFF' }} data-testid="wb-ai-preview-canvas">
      <svg viewBox={`0 0 ${WB_W} ${WB_H}`} className="w-full h-auto block" aria-label="AI proposal preview">
        <WhiteboardShapes objects={objects} />
      </svg>
    </div>
  );
}

function OpRow({ checked, onToggle, icon: Icon, title, cost, testid, children }) {
  return (
    <div className={`rounded-md border p-3 space-y-2 ${checked ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}>
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" className="mt-1 h-4 w-4" checked={checked} onChange={(e) => onToggle(e.target.checked)} data-testid={testid} />
        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-600" />
        <span className="min-w-0">
          <span className="block text-sm font-medium">{title}</span>
          <span className="block text-[11px] text-muted-foreground">{cost}</span>
        </span>
      </label>
      {checked ? children : null}
    </div>
  );
}

// Lists every paid AI call before anything runs. The user ticks what to run; no hidden or automatic calls.
export function PaidPlan({ plan, choice, setChoice }) {
  const set = (patch) => setChoice({ ...choice, ...patch });
  const n = (choice.diagram ? 1 : 0) + (choice.illustration ? 1 : 0);
  return (
    <div className="space-y-3" data-testid="wb-ai-plan">
      <h4 className="text-sm font-semibold">Confirm paid AI operations</h4>
      {plan.needs_diagram ? (
        <OpRow checked={choice.diagram} onToggle={(v) => set({ diagram: v })} icon={Sparkles} testid="wb-ai-op-diagram"
          title="Editable diagram" cost="1 text AI call. Shapes, labels and arrows you can edit." />
      ) : null}
      {plan.illustration ? (
        <OpRow checked={choice.illustration} onToggle={(v) => set({ illustration: v })} icon={ImageIcon} testid="wb-ai-op-illustration"
          title="AI illustration (picture layer)" cost="1 image AI call with the premium image model. Uses more credits than text.">
          <Input value={choice.subject} maxLength={1000} onChange={(e) => set({ subject: e.target.value })} data-testid="wb-ai-ill-subject" className="h-9 text-sm" />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <select value={choice.placement} onChange={(e) => set({ placement: e.target.value })} data-testid="wb-ai-ill-placement"
              className="h-8 rounded-md border border-input bg-background px-2 text-xs">
              <option value="background">Behind the content</option>
              <option value="foreground">In front, as an accent</option>
            </select>
            <label className="flex items-center gap-2">
              <Switch checked={choice.transparent} onCheckedChange={(v) => set({ transparent: v })} data-testid="wb-ai-ill-transparent" />
              Transparent background
            </label>
          </div>
        </OpRow>
      ) : null}
      <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="wb-ai-paid-summary">
        This will make <b>{n} paid AI call{n === 1 ? '' : 's'}</b> and may use credits. Nothing is retried automatically,
        and nothing changes on your board until you accept the preview.
      </p>
    </div>
  );
}

export function IllustrationInfo({ ill, variant, setVariant }) {
  if (!ill) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="wb-ai-ill-info">
      <span className="flex items-start gap-2 min-w-0 flex-1"><ImageIcon className="h-4 w-4 flex-shrink-0" />
      <span className="min-w-0 flex-1">
        AI illustration: a <b>picture layer</b> {ill.placement === 'foreground' ? 'in front of' : 'behind'} the content. You can move, resize, rotate, fade and reorder it; its pixels are not editable shapes.
        {ill.warnings?.length ? ` ${ill.warnings.join(' ')}` : ''}
      </span></span>
      {ill.original_url ? (
        <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid="wb-ai-ill-variant" className="h-8 rounded-md border border-sky-300 bg-white px-2">
          <option value="layer">Transparent cut-out</option>
          <option value="original">Keep original background</option>
        </select>
      ) : null}
    </div>
  );
}
